// src/features/business/businessSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api/requests';
import log from '../../logger'; // Import the logger

// Fetch businesses with pagination & filters
export const fetchBusinesses = createAsyncThunk(
  'business/fetchBusinesses',
  async ({ page = 1, limit = 10, filters = {} }, { rejectWithValue }) => {
    try {
      log.info('Fetching businesses with page:', page, 'and limit:', limit); // Log the fetch action
      const response = await api.businesses.getAll({ page, limit, ...filters });
      if (response.data.status) {
        log.info('Businesses fetched successfully:', response.data.data); // Log successful fetch
        return response.data.data; // Adjust based on your API response structure
      } else {
        log.warn('Failed to fetch businesses:', response.data.message); // Log warning on failure
        return rejectWithValue(response.data.message);
      }
    } catch (error) {
      log.error('Error fetching businesses:', error); // Log the error
      return rejectWithValue(error.response?.data?.message || 'Error fetching businesses');
    }
  }
);

// Search businesses with keyword
export const searchBusinesses = createAsyncThunk(
  'business/searchBusinesses',
  async ({ keyword, page = 1, limit = 10, filters = {} }, { rejectWithValue }) => {
    try {
      log.info('Searching businesses with keyword:', keyword);
      const response = await api.businesses.searchByKeyword({ keyword, page, limit, ...filters });
      if (response.data.status) {
        log.info('Businesses search completed successfully:', response.data.data);
        return response.data.data;
      } else {
        log.warn('Failed to search businesses:', response.data.message);
        return rejectWithValue(response.data.message);
      }
    } catch (error) {
      log.error('Error searching businesses:', error);
      return rejectWithValue(error.response?.data?.message || 'Error searching businesses');
    }
  }
);

// Create new business
export const createBusiness = createAsyncThunk(
  'business/createBusiness',
  async (formData, { rejectWithValue }) => {
    try {
      log.info('Creating business with data:', formData); // Log the creation action
      const response = await api.businesses.create(formData); // Use the create method
      if (response.data.status) {
        log.info('Business created successfully:', response.data.data); // Log successful creation
        return response.data.data; // Return the created business
      } else {
        log.warn('Failed to create business:', response.data.message); // Log warning on failure
        return rejectWithValue(response.data.message); // Return error message
      }
    } catch (error) {
      log.error('Error creating business:', error); // Log the error
      return rejectWithValue(error.response?.data?.message || 'Failed to create business');
    }
  }
);

// Update business
export const updateBusiness = createAsyncThunk(
  'business/updateBusiness',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      log.info('Updating business with ID:', id, 'and data:', data); // Log the update action
      const response = await api.businesses.update(id, data); // Use the update method with data parameter
      if (response.data.status) {
        log.info('Business updated successfully:', response.data.data); // Log successful update
        return response.data.data; // Return the updated business
      } else {
        log.warn('Failed to update business:', response.data.message); // Log warning on failure
        return rejectWithValue(response.data.message);
      }
    } catch (error) {
      log.error('Error updating business:', error); // Log the error
      return rejectWithValue(error.response?.data?.message || 'Error updating business');
    }
  }
);

// Delete business
export const deleteBusiness = createAsyncThunk(
  'business/deleteBusiness',
  async (id, { rejectWithValue }) => {
    try {
      log.info('Deleting business with ID:', id); // Log the delete action
      const response = await api.businesses.delete(id); // Use the delete method
      if (response.data.status) {
        log.info('Business deleted successfully:', id); // Log successful deletion
        return id; // return the id of the deleted business
      } else {
        log.warn('Failed to delete business:', response.data.message); // Log warning on failure
        return rejectWithValue(response.data.message);
      }
    } catch (error) {
      log.error('Error deleting business:', error); // Log the error
      return rejectWithValue(error.response?.data?.message || 'Error deleting business');
    }
  }
);

// Bulk delete businesses
export const bulkDeleteBusinesses = createAsyncThunk(
  'business/bulkDeleteBusinesses',
  async (ids, { rejectWithValue }) => {
    try {
      await Promise.all(ids.map(id => api.businesses.delete(id)));
      return ids;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Error deleting businesses');
    }
  }
);

// Bulk activate/deactivate businesses
export const bulkUpdateBusinessStatus = createAsyncThunk(
  'business/bulkUpdateBusinessStatus',
  async ({ ids, status }, { rejectWithValue }) => {
    try {
      await api.businesses.bulkUpdateStatus(ids, status);
      return { ids, status };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Error updating business status');
    }
  }
);

// Get business by ID
export const getBusinessById = createAsyncThunk(
  'business/getBusinessById',
  async (id, { rejectWithValue }) => {
    try {
      log.info('Fetching business with ID:', id); // Log the fetch action
      const response = await api.businesses.getById(id); // Use the getById method
      if (response.data.status) {
        log.info('Business fetched successfully:', response.data.data); // Log successful fetch
        return response.data.data; // Return the business data
      } else {
        log.warn('Failed to fetch business:', response.data.message); // Log warning on failure
        return rejectWithValue(response.data.message);
      }
    } catch (error) {
      log.error('Error fetching business:', error); // Log the error
      return rejectWithValue(error.response?.data?.message || 'Error fetching business');
    }
  }
);

const businessSlice = createSlice({
  name: 'business',
  initialState: {
    items: [],
    total: 0,
    page: 1,
    pages: 0,
    limit: 10,
    loading: false,
    error: null,
    business: null,
    // you can store a single selected business too if needed
  },
  reducers: {
    // Add a reducer to clear business data when leaving the edit page
    clearBusinessData: (state) => {
      state.business = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBusinesses.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBusinesses.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        const { businesses, total, page, pages, limit } = action.payload;
        // Ensure each business has a region field
        state.items = businesses.map(business => ({
          ...business,
          region: business.region || null
        }));
        state.total = total;
        state.page = page;
        state.pages = pages;
        state.limit = limit;
        // Clear the single business data when fetching the list
        state.business = null;
      })
      .addCase(fetchBusinesses.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(searchBusinesses.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(searchBusinesses.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        const { businesses, total, page, pages, limit } = action.payload;
        // Ensure each business has a region field
        state.items = businesses.map(business => ({
          ...business,
          region: business.region || null
        }));
        state.total = total;
        state.page = page;
        state.pages = pages;
        state.limit = limit;
        // Clear the single business data when fetching the list
        state.business = null;
      })
      .addCase(searchBusinesses.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createBusiness.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createBusiness.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.items.push(action.payload);
      })
      .addCase(createBusiness.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateBusiness.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateBusiness.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        const index = state.items.findIndex(item => item._id === action.payload._id);
        if (index !== -1) {
          state.items[index] = action.payload; // Update the existing business
        }
        // Update the single business data as well
        state.business = action.payload;
      })
      .addCase(updateBusiness.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(deleteBusiness.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteBusiness.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.items = state.items.filter(item => item._id !== action.payload); // Remove the deleted business
        // Clear the business data if it was the deleted one
        if (state.business && state.business._id === action.payload) {
          state.business = null;
        }
      })
      .addCase(deleteBusiness.rejected, (state) => {
        state.loading = false;
      })
      .addCase(bulkDeleteBusinesses.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(bulkDeleteBusinesses.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(bulkDeleteBusinesses.rejected, (state) => {
        state.loading = false;
      })
      .addCase(bulkUpdateBusinessStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(bulkUpdateBusinessStatus.fulfilled, (state, action) => {
        state.loading = false;
        const { ids, status } = action.payload;
        state.items = state.items.map(item => ids.includes(item._id) ? { ...item, status } : item);
      })
      .addCase(bulkUpdateBusinessStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(getBusinessById.pending, (state) => {
        state.loading = true;
        state.error = null;
        // Clear existing business data when fetching a new one
        state.business = null;
      })
      .addCase(getBusinessById.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        // Ensure the business has a region field
        state.business = {
          ...action.payload,
          region: action.payload.region || null
        };
      })
      .addCase(getBusinessById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.business = null;
      });
  },
});

export const { clearBusinessData } = businessSlice.actions;
export default businessSlice.reducer;
